// src/components/forms/EditUser.jsx
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import { useAuth } from '../../context/AuthContext';
import { medicalCenterService } from '../../services/medicalCenterService';
import { userService } from '../../services/userService';

const EditUser = ({ isOpen, onClose, onSuccess, user = null }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    medical_center_id: '',
    role: '',
    first_name: '',
    last_name: ''
  });
  const [medicalCenters, setMedicalCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pre-populate form when user data is available
  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        email: user.email || '',
        medical_center_id: user.medical_center_id || '',
        role: user.role || user.roles?.[0]?.name || '',
        first_name: user.first_name || '',
        last_name: user.last_name || ''
      });
    }
  }, [user, isOpen]);

  // Load medical centers when modal opens
  useEffect(() => {
    if (isOpen) {
      loadMedicalCenters();
    }
  }, [isOpen]);

  const loadMedicalCenters = async () => {
    try {
      const centers = await medicalCenterService.getMedicalCenters();
      setMedicalCenters(centers);
    } catch (err) {
      console.error('Error loading medical centers:', err);
      setError('Failed to load medical centers');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare the data for update
      const updateData = {
        email: formData.email,
        medical_center_id: formData.medical_center_id,
        role: formData.role,
        first_name: formData.first_name,
        last_name: formData.last_name
      };

      // Call the update service
      await userService.updateUser(user.id, updateData);
      
      onSuccess('User updated successfully!');
      onClose();
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      medical_center_id: '',
      role: '',
      first_name: '',
      last_name: ''
    });
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit User">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            placeholder="Enter email address"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Medical Center *
          </label>
          <select
            name="medical_center_id"
            value={formData.medical_center_id}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select Medical Center</option>
            {medicalCenters.map(center => (
              <option key={center.id} value={center.id}>
                {center.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Role *
          </label>
          <select
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select Role</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="doctor">Doctor</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <Input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
            placeholder="Enter first name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <Input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
            placeholder="Enter last name"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditUser;